import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database helper functions
export const db = {
  // User functions
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  },

  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error getting user profile:', error)
      throw error
    }
  },

  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  // Reminder functions
  async createReminder(userId, text, mood) {
    const { data, error } = await supabase
      .from('reminders')
      .insert({
        user_id: userId,
        text: text,
        mood: mood
      })
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async getUserReminders(userId) {
    const { data, error } = await supabase
      .from('reminders')
      .select(`
        *,
        rewrites (
          *,
          rewriter:users!rewrites_rewriter_id_fkey (
            id,
            name,
            avatar_url
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },

  async getRandomUnrewrittenReminder(excludeUserId) {
    const { data, error } = await supabase
      .from('reminders')
      .select(`
        *,
        user:users!reminders_user_id_fkey (
          id,
          name,
          avatar_url
        )
      `)
      .eq('rewritten', false)
      .neq('user_id', excludeUserId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
    
    if (error) throw error
    return data
  },

  // Rewrite functions
  async createRewrite(reminderId, rewriterId, text) {
    const { data, error } = await supabase
      .from('rewrites')
      .insert({
        reminder_id: reminderId,
        rewriter_id: rewriterId,
        text: text
      })
      .select()
      .single()
    
    if (error) throw error

    // Mark reminder as rewritten
    await supabase
      .from('reminders')
      .update({ rewritten: true })
      .eq('id', reminderId)
    
    return data
  },

  async getUserRewrites(userId) {
    const { data, error } = await supabase
      .from('rewrites')
      .select(`
        *,
        reminder:reminders!rewrites_reminder_id_fkey (
          text,
          mood,
          user:users!reminders_user_id_fkey (
            name
          )
        )
      `)
      .eq('rewriter_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },

  // Vote functions
  async voteRewrite(rewriteId, userId) {
    try {
      // Check if vote already exists
      const { data: existingVote, error: checkError } = await supabase
        .from('votes')
        .select('*')
        .eq('rewrite_id', rewriteId)
        .eq('user_id', userId)
        .single()
      
      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found"
        throw checkError
      }
      
      if (existingVote) {
        // Remove vote (toggle behavior)
        const { error: deleteError } = await supabase
          .from('votes')
          .delete()
          .eq('rewrite_id', rewriteId)
          .eq('user_id', userId)
        
        if (deleteError) throw deleteError
        return { action: 'removed' }
      } else {
        // Add vote
        const { data, error } = await supabase
          .from('votes')
          .insert({
            rewrite_id: rewriteId,
            user_id: userId
          })
          .select()
          .single()
        
        if (error) throw error
        return { action: 'added', data }
      }
    } catch (error) {
      console.error('Error voting for rewrite:', error)
      // If votes table doesn't exist, just return success (graceful degradation)
      if (error.message?.includes('relation "votes" does not exist')) {
        return { action: 'added', data: null }
      }
      throw error
    }
  },

  async getTopRewrites() {
    try {
      // First get all rewrites with their related data
      const { data: rewrites, error: rewritesError } = await supabase
        .from('rewrites')
        .select(`
          *,
          rewriter:users!rewrites_rewriter_id_fkey (
            id,
            name,
            avatar_url
          ),
          reminder:reminders!rewrites_reminder_id_fkey (
            text,
            mood
          )
        `)
        .order('created_at', { ascending: false })
        .limit(20)
      
      if (rewritesError) throw rewritesError
      
      // Get vote counts for each rewrite
      const { data: votes, error: votesError } = await supabase
        .from('votes')
        .select('rewrite_id')
        .in('rewrite_id', rewrites.map(r => r.id))
      
      if (votesError) throw votesError
      
      // Count votes per rewrite
      const voteCountMap = votes.reduce((acc, vote) => {
        acc[vote.rewrite_id] = (acc[vote.rewrite_id] || 0) + 1
        return acc
      }, {})
      
      // Combine rewrite data with vote counts and sort by votes
      const rewritesWithVotes = rewrites.map(rewrite => ({
        ...rewrite,
        votes: voteCountMap[rewrite.id] || 0
      })).sort((a, b) => b.votes - a.votes)
      
      return rewritesWithVotes.slice(0, 10) // Return top 10
    } catch (error) {
      console.error('Error getting top rewrites:', error)
      return []
    }
  },

  // Achievement functions
  async checkAndAwardAchievements(userId) {
    const user = await this.getUserProfile(userId)
    const rewrites = await this.getUserRewrites(userId)
    const reminders = await this.getUserReminders(userId)
    
    const achievements = []
    
    // Check for various achievements
    if (rewrites.length >= 10 && !user.achievements?.includes('10_rewrites')) {
      achievements.push('10_rewrites')
    }
    
    if (reminders.some(r => r.rewrites?.length > 0) && !user.achievements?.includes('first_received')) {
      achievements.push('first_received')
    }
    
    if (rewrites.some(r => r.votes >= 10) && !user.achievements?.includes('popular_rewriter')) {
      achievements.push('popular_rewriter')
    }
    
    if (achievements.length > 0) {
      const newAchievements = [...(user.achievements || []), ...achievements]
      await this.updateUserProfile(userId, { achievements: newAchievements })
    }
    
    return achievements
  }
} 